// 走行データ（仕様書「走行データ」）：日・週・月の集計（連続運転の最長を含む）、日ごとの棒グラフ（距離／運転・拘束時間と上限の線）、事務所の平均との比べ、日ごとの表と CSV。
// 1 日だけを見るときは、最高・平均の速さと時速 100km 以上が 1 分続いた区間（スマホの GPS の目安。速度違反の証拠にはしない）
import { useEffect, useState } from 'react';
import { RULES, jstDate, type DrivingDay, type DrivingReport } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { toCsv } from '../lib/csv';

type Span = 'day' | 'week' | 'month';
const hm = (min: number) => `${Math.floor(min / 60)}:${String(Math.round(min % 60)).padStart(2, '0')}`;
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const rangeOf = (span: Span, base: string) => {
  if (span === 'day') return { from: base, to: base };
  if (span === 'week') { const w = new Date(`${base}T00:00:00Z`).getUTCDay(); const from = addDays(base, -((w + 6) % 7)); return { from, to: addDays(from, 6) }; }
  const [y, m] = base.split('-').map(Number) as [number, number];
  return { from: `${base.slice(0, 7)}-01`, to: new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) };
};
const time = (iso: string) => { const d = new Date(iso); return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`; };

export function DrivingPanel({ driverId, name }: { driverId: string; name: string }) {
  const today = jstDate(new Date().toISOString());
  const [span, setSpan] = useState<Span>('day');
  const [base, setBase] = useState(today);
  const [r, setR] = useState<DrivingReport | null>(null);
  const [err, setErr] = useState('');
  const { from, to } = rangeOf(span, base);
  useEffect(() => {
    let alive = true;
    setR(null); setErr('');
    api.get<DrivingReport>(`/v1/office/drivers/${driverId}/driving?from=${from}&to=${to}`).then((x) => { if (alive) setR(x); })
      .catch((e) => { if (alive) setErr(e instanceof HttpError ? e.message : '読み込めませんでした'); });
    return () => { alive = false; };
  }, [driverId, from, to]);
  const csv = () => {
    if (!r) return;
    const head = ['日付', '走行距離（km）', '運転', '連続運転の最長', '配送作業', '休憩', '理由不明の停車', '拘束時間', '配送', '必着の数', '間に合った数'];
    const rows = r.days.map((d) => [d.date, String(d.distanceKm), hm(d.drivingMin), hm(d.maxContinuousMin), hm(d.workMin), hm(d.breakMin), hm(d.unknownStopMin), hm(d.restraintMin), String(d.deliveries), String(d.dueTotal), String(d.dueOnTime)]);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([toCsv([head, ...rows])], { type: 'text/csv' }));
    a.download = `走行データ_${name}_${from}_${to}.csv`;
    a.click();
  };
  const t = r?.totals, avg = r?.officeAvg;
  const worked = r?.days.filter((d) => d.restraintMin > 0).length ?? 0;
  const per = (v: number) => (worked ? v / worked : 0);
  // この欄だけを印刷する（styles.css の print-one）
  const print = () => {
    document.body.classList.add('print-one');
    window.addEventListener('afterprint', () => document.body.classList.remove('print-one'), { once: true });
    window.print();
  };
  return (
    <section className="panel print-me" aria-label="走行データ" style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>走行データ<span className="print-only">　{name}</span></b>
        <span style={{ flex: 1 }} />
        <div role="tablist" className="no-print" style={{ display: 'flex', gap: 4 }}>
          {(['day', 'week', 'month'] as const).map((k) => <button key={k} role="tab" aria-selected={span === k} className={`btn sm ${span === k ? 'pri' : ''}`} onClick={() => setSpan(k)}>{{ day: '日', week: '週', month: '月' }[k]}</button>)}
        </div>
        <input type="date" className="no-print" aria-label="走行データの日付" value={base} max={today} onChange={(e) => e.target.value && setBase(e.target.value)} style={{ width: 160 }} />
        <button className="btn sm no-print" disabled={!r} onClick={csv}>CSV</button>
        <button className="btn sm no-print" disabled={!r} onClick={print}>印刷</button>
      </div>
      <small className="muted">{from === to ? from : `${from}〜${to}`}<span className="no-print">。見たことは操作ログに残ります</span></small>
      {err && <p className="err" role="alert">{err}</p>}
      {!r ? !err && <p className="muted">読み込み中…</p> : <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }} aria-label="集計">
          {([
            ['走行距離', `${t!.distanceKm} km`, avg!.distanceKm, per(t!.distanceKm), 'km'],
            ['運転時間', hm(t!.drivingMin), avg!.drivingMin, per(t!.drivingMin), 'min'],
            ['連続運転の最長', hm(t!.maxContinuousMin), null, null, ''],
            ['配送作業', hm(t!.workMin), avg!.workMin, per(t!.workMin), 'min'],
            ['休憩', hm(t!.breakMin), avg!.breakMin, per(t!.breakMin), 'min'],
            ['理由不明の停車', hm(t!.unknownStopMin), avg!.unknownStopMin, per(t!.unknownStopMin), 'min'],
            ['拘束時間', hm(t!.restraintMin), avg!.restraintMin, per(t!.restraintMin), 'min'],
            ['配送件数', `${t!.deliveries} 件`, avg!.deliveries, per(t!.deliveries), 'n'],
            ['必着の達成', t!.dueTotal ? `${Math.round((t!.dueOnTime / t!.dueTotal) * 100)}%（${t!.dueOnTime}/${t!.dueTotal}）` : '—', null, null, ''],
          ] as [string, string, number | null, number | null, string][]).map(([label, value, a, mine, unit]) => (
            <div key={label} style={{ background: 'var(--surface-2, #f6f6f6)', borderRadius: 10, padding: 10 }}>
              <small className="muted">{label}</small>
              <b style={{ display: 'block', fontSize: 18 }}>{value}</b>
              {a !== null && mine !== null && worked > 0 && <small className="muted">1 日あたり {unit === 'km' ? `${Math.round(mine * 10) / 10} km` : unit === 'n' ? `${Math.round(mine * 10) / 10} 件` : hm(mine)}・事務所の平均 {unit === 'km' ? `${a} km` : unit === 'n' ? `${a} 件` : hm(a)}</small>}
            </div>
          ))}
        </div>
        {r.speed && (
          <div style={{ fontSize: 13 }} aria-label="速さ">
            速さ（スマホの GPS の目安。速度違反の証拠にはしません）：最高 {r.speed.maxKmh ?? '—'} km/h・平均 {r.speed.avgKmh ?? '—'} km/h
            {r.speed.fast.length > 0 && <span className="err">・時速 {RULES.fastKmh}km 以上が 1 分続いた区間 {r.speed.fast.length} か所（{r.speed.fast.map((f) => `${time(f.start)}〜${time(f.end)} 最高 ${f.maxKmh}km/h`).join('、')}）</span>}
          </div>
        )}
        {r.days.length > 1 && <Bars days={r.days} />}
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead><tr><th>日付</th><th>走行距離</th><th>運転</th><th>連続運転の最長</th><th>配送作業</th><th>休憩</th><th>理由不明の停車</th><th>拘束時間</th><th>配送</th><th>必着</th></tr></thead>
            <tbody>{r.days.filter((d) => d.restraintMin > 0 || d.deliveries > 0).length === 0 ? <tr><td colSpan={10} className="muted">この期間の勤務はありません</td></tr>
              : r.days.filter((d) => d.restraintMin > 0 || d.deliveries > 0).map((d) => (
                <tr key={d.date}><td className="num">{d.date.slice(5).replace('-', '/')}</td><td className="num">{d.distanceKm} km</td><td className="num">{hm(d.drivingMin)}</td><td className={`num${d.maxContinuousMin >= RULES.continuousDriveAlertMin ? ' err' : ''}`}>{hm(d.maxContinuousMin)}</td><td className="num">{hm(d.workMin)}</td><td className="num">{hm(d.breakMin)}</td>
                  <td className={`num${d.unknownStopMin ? ' err' : ''}`}>{hm(d.unknownStopMin)}</td><td className={`num${d.restraintMin > RULES.dailyRestraintAlertMin ? ' err' : ''}`}>{hm(d.restraintMin)}</td><td className="num">{d.deliveries} 件</td><td className="num">{d.dueTotal ? `${d.dueOnTime}/${d.dueTotal}` : '—'}</td></tr>
              ))}</tbody>
          </table>
        </div>
      </>}
    </section>
  );
}

/** 日ごとの棒グラフ。目盛りの違う値は別のグラフにする（距離／運転・拘束時間） */
function Bars({ days }: { days: DrivingDay[] }) {
  const W = Math.max(320, days.length * 22), H = 120, P = 24;
  const bw = Math.max(4, (W - P) / days.length - 4);
  const xs = (i: number) => P + i * ((W - P) / days.length) + 2;
  const maxKm = Math.max(10, ...days.map((d) => d.distanceKm));
  const maxH = Math.max(RULES.dailyRestraintMaxMin, ...days.map((d) => d.restraintMin)) / 60;
  const y = (v: number, max: number) => H - (v / max) * (H - 16);
  const limit = RULES.dailyRestraintAlertMin / 60;
  // 日付の目盛り：1 週間までは毎日、それより長ければ 1・8・15・22・29 日
  const showLabel = (i: number, d: DrivingDay) => days.length <= 7 || Number(d.date.slice(8)) % 7 === 1 || (i === 0 && Number(d.date.slice(8)) % 7 > 4);
  return (
    <div style={{ display: 'grid', gap: 12, overflowX: 'auto' }}>
      <figure style={{ margin: 0 }} aria-label="日ごとの走行距離">
        <figcaption className="muted" style={{ fontSize: 12 }}>走行距離（km）</figcaption>
        <svg width={W} height={H + 18} role="img" aria-label="日ごとの走行距離の棒グラフ">
          <line x1={P} x2={W} y1={H} y2={H} stroke="#ccc" />
          <text x={0} y={y(maxKm, maxKm) + 4} fontSize={10} fill="#888">{Math.round(maxKm)}</text>
          {days.map((d, i) => d.distanceKm > 0 && <rect key={d.date} x={xs(i)} y={y(d.distanceKm, maxKm)} width={bw} height={H - y(d.distanceKm, maxKm)} rx={2} fill="#111"><title>{`${d.date}：${d.distanceKm} km`}</title></rect>)}
          {days.map((d, i) => showLabel(i, d) && <text key={`l${d.date}`} x={xs(i)} y={H + 14} fontSize={10} fill="#888">{Number(d.date.slice(8))}</text>)}
        </svg>
      </figure>
      <figure style={{ margin: 0 }} aria-label="日ごとの運転時間と拘束時間">
        <figcaption className="muted" style={{ fontSize: 12 }}>時間：<span style={{ display: 'inline-block', width: 10, height: 10, background: '#bbb', borderRadius: 2 }} /> 拘束時間　<span style={{ display: 'inline-block', width: 10, height: 10, background: '#111', borderRadius: 2 }} /> 運転時間　<span style={{ color: 'var(--alert)' }}>―― 拘束 {limit} 時間（警告）</span></figcaption>
        <svg width={W} height={H + 18} role="img" aria-label="日ごとの運転時間と拘束時間の棒グラフ">
          <line x1={P} x2={W} y1={H} y2={H} stroke="#ccc" />
          <line x1={P} x2={W} y1={y(limit, maxH)} y2={y(limit, maxH)} stroke="var(--alert)" strokeDasharray="4 3" />
          <text x={0} y={y(limit, maxH) + 4} fontSize={10} fill="var(--alert)">{limit}h</text>
          {days.map((d, i) => d.restraintMin > 0 && <g key={d.date}>
            <rect x={xs(i)} y={y(d.restraintMin / 60, maxH)} width={bw} height={H - y(d.restraintMin / 60, maxH)} rx={2} fill="#bbb"><title>{`${d.date}：拘束 ${hm(d.restraintMin)}・運転 ${hm(d.drivingMin)}`}</title></rect>
            <rect x={xs(i) + bw / 4} y={y(d.drivingMin / 60, maxH)} width={bw / 2} height={H - y(d.drivingMin / 60, maxH)} rx={2} fill="#111"><title>{`${d.date}：運転 ${hm(d.drivingMin)}`}</title></rect>
          </g>)}
          {days.map((d, i) => showLabel(i, d) && <text key={`l${d.date}`} x={xs(i)} y={H + 14} fontSize={10} fill="#888">{Number(d.date.slice(8))}</text>)}
        </svg>
      </figure>
    </div>
  );
}
